<template>
  <div class="w-full max-w-md mx-auto select-none font-sans">
    <!-- 难度切换选项卡（仅限：简单 / 中等 / 困难 / 专家） -->
    <div class="flex items-center justify-between gap-1 mb-4 bg-secondary/50 p-1 rounded-xl border border-border">
      <button
        v-for="d in difficulties"
        :key="d.key"
        type="button"
        @click="selectDifficulty(d.key)"
        class="flex-1 py-1.5 text-xs sm:text-sm font-semibold rounded-lg transition"
        :class="currentDifficulty === d.key ? 'bg-white text-primary shadow-xs' : 'text-muted hover:text-clay'"
      >
        {{ d.label }}
      </button>
    </div>

    <!-- 顶栏状态：计时与重开 -->
    <div class="flex items-center justify-between mb-3 px-1">
      <div class="flex items-center gap-2 text-xs text-muted">
        <span>难度：<strong class="text-clay">{{ currentDifficultyLabel }}</strong></span>
        <span>•</span>
        <span>用时：<strong class="text-clay font-mono">{{ formattedTime }}</strong></span>
      </div>
      <div class="flex items-center gap-3">
        <button
          type="button"
          @click="shareCurrent(isCompleted)"
          class="text-xs text-primary hover:underline font-medium"
        >
          分享
        </button>
        <button
          @click="initNewGame"
          type="button"
          class="text-xs text-primary hover:underline font-medium"
        >
          换一局题目
        </button>
      </div>
    </div>

    <!-- 9x9 数独主棋盘 -->
    <div
      class="relative aspect-square w-full rounded-2xl bg-white p-1.5 sm:p-2 border-2 border-primary shadow-sm"
    >
      <div class="grid grid-cols-9 grid-rows-9 h-full w-full border border-gray-300">
        <template v-for="(row, r) in grid" :key="r">
          <div
            v-for="(cell, c) in row"
            :key="`${r}-${c}`"
            @click="selectCell(r, c)"
            class="relative flex items-center justify-center cursor-pointer transition-colors"
            :class="[
              c % 3 === 2 && c !== 8 ? 'border-r-2 border-primary/80' : 'border-r border-gray-200',
              r % 3 === 2 && r !== 8 ? 'border-b-2 border-primary/80' : 'border-b border-gray-200',
              cell.isConflict
                ? 'bg-red-100 text-seal'
                : isSelected(r, c)
                  ? 'bg-primary/20'
                  : isRelated(r, c)
                    ? 'bg-secondary/40'
                    : 'bg-white',
            ]"
          >
            <!-- 预置/玩家填写的数字 -->
            <span
              v-if="cell.value > 0"
              class="text-base sm:text-xl md:text-2xl font-bold"
              :class="[
                cell.fixed ? 'text-clay font-extrabold' : 'text-primary font-semibold',
                cell.isConflict ? 'text-seal' : '',
              ]"
            >
              {{ cell.value }}
            </span>

            <!-- 草稿候选数 (九宫格微缩排布) -->
            <div
              v-else-if="cell.notes.length > 0"
              class="grid grid-cols-3 grid-rows-3 h-full w-full p-0.5 pointer-events-none text-[8px] sm:text-[10px] text-muted font-medium leading-none text-center"
            >
              <span v-for="n in 9" :key="n" class="flex items-center justify-center">
                {{ cell.notes.includes(n) ? n : '' }}
              </span>
            </div>
          </div>
        </template>
      </div>

      <!-- 挑战成功弹层 -->
      <div
        v-if="isCompleted"
        class="absolute inset-0 bg-primary/90 backdrop-blur-xs rounded-2xl flex flex-col items-center justify-center p-6 text-center z-20 animate-fade-in"
      >
        <div class="text-3xl font-extrabold text-white mb-2">🎉 挑战成功！</div>
        <p class="text-sm text-white/90 mb-5">恭喜你解开了这道 {{ currentDifficultyLabel }} 级数独题，用时 {{ formattedTime }}</p>
        <div class="flex gap-3">
          <button
            type="button"
            @click="shareCurrent(true)"
            class="h-10 px-4 rounded-lg bg-white text-primary font-bold text-sm shadow hover:bg-white/90 transition"
          >
            炫耀战绩
          </button>
          <button
            @click="initNewGame"
            class="h-10 px-6 rounded-lg bg-white text-primary font-bold text-sm shadow-lg hover:bg-white/90 active:scale-95 transition"
          >
            再来一局
          </button>
        </div>
      </div>
    </div>

    <!-- 底部操作栏：笔记开关、擦除、数字输入键 -->
    <div class="mt-4 flex flex-col gap-3">
      <!-- 模式与工具 -->
      <div class="flex items-center justify-center gap-3">
        <button
          type="button"
          @click="toggleNoteMode"
          class="flex items-center gap-1.5 h-9 px-4 rounded-lg text-xs font-semibold border transition"
          :class="isNoteMode ? 'bg-seal text-white border-seal' : 'bg-white text-clay border-border hover:bg-secondary'"
        >
          <span>🖊️ 草稿笔记</span>
          <span class="text-[10px] px-1.5 py-0.2 rounded bg-black/10">{{ isNoteMode ? '开启' : '关闭' }}</span>
        </button>

        <button
          type="button"
          @click="eraseSelected"
          class="flex items-center gap-1 h-9 px-4 rounded-lg text-xs font-semibold bg-white text-clay border border-border hover:bg-secondary transition"
        >
          <span>🗑️ 擦除</span>
        </button>
      </div>

      <!-- 1-9 数字按键 -->
      <div class="grid grid-cols-9 gap-1 sm:gap-1.5">
        <button
          v-for="num in 9"
          :key="num"
          @click="inputNumber(num)"
          type="button"
          class="aspect-square rounded-lg bg-cream font-bold text-base sm:text-lg text-clay border border-border hover:bg-primary hover:text-white active:scale-95 transition flex items-center justify-center shadow-2xs"
        >
          {{ num }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { trackLevelStart } from '@/analytics/track'
import { createPuzzle, type DifficultyKey } from './puzzle'
import { shareSudoku } from '@/vue-apps/share/games'

interface Cell {
  r: number
  c: number
  value: number
  fixed: boolean
  notes: number[]
  isConflict: boolean
}

const difficulties: { key: DifficultyKey; label: string }[] = [
  { key: 'easy', label: '简单' },
  { key: 'medium', label: '中等' },
  { key: 'hard', label: '困难' },
  { key: 'expert', label: '专家' },
]

const currentDifficulty = ref<DifficultyKey>('medium')
const currentDifficultyLabel = computed(() => {
  return difficulties.find((d) => d.key === currentDifficulty.value)?.label || '中等'
})

const grid = ref<Cell[][]>([])
const selectedCell = ref<{ r: number; c: number } | null>(null)
const isNoteMode = ref(false)
const isCompleted = ref(false)

// 计时器
const secondsElapsed = ref(0)
let timerInterval: ReturnType<typeof setInterval> | null = null

const stopTimer = () => {
  if (timerInterval == null) return
  clearInterval(timerInterval)
  timerInterval = null
}

const shareCurrent = (withTime: boolean) => {
  shareSudoku(currentDifficulty.value, withTime ? secondsElapsed.value : null)
}

const formattedTime = computed(() => {
  const m = Math.floor(secondsElapsed.value / 60)
  const s = secondsElapsed.value % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
})

const isFormField = (event: KeyboardEvent) => {
  const el = event.target
  if (!(el instanceof HTMLElement)) return false
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable
}

// 初始化新游戏。题目在计时开始前生成，保证盘面已是唯一解。
const initNewGame = () => {
  const puzzle = createPuzzle(currentDifficulty.value)
  isCompleted.value = false
  secondsElapsed.value = 0
  stopTimer()
  timerInterval = setInterval(() => {
    secondsElapsed.value++
  }, 1000)

  const newGrid: Cell[][] = []
  for (let r = 0; r < 9; r++) {
    const row: Cell[] = []
    for (let c = 0; c < 9; c++) {
      const val = puzzle[r][c]
      row.push({
        r,
        c,
        value: val,
        fixed: val > 0,
        notes: [],
        isConflict: false,
      })
    }
    newGrid.push(row)
  }

  grid.value = newGrid
  selectedCell.value = null
  validateGrid()
  trackLevelStart({ level_name: 'sudoku', difficulty: currentDifficulty.value })
}

// 切换难度
const selectDifficulty = (key: DifficultyKey) => {
  if (currentDifficulty.value === key) return
  currentDifficulty.value = key
  initNewGame()
}

// 选中单元格
const selectCell = (r: number, c: number) => {
  selectedCell.value = { r, c }
}

const isSelected = (r: number, c: number) => {
  return selectedCell.value?.r === r && selectedCell.value?.c === c
}

const isRelated = (r: number, c: number) => {
  if (!selectedCell.value) return false
  const sel = selectedCell.value
  if (sel.r === r || sel.c === c) return true
  const sameBox = Math.floor(sel.r / 3) === Math.floor(r / 3) && Math.floor(sel.c / 3) === Math.floor(c / 3)
  if (sameBox) return true
  // 相同数字微高亮
  const activeVal = grid.value[sel.r]?.[sel.c]?.value
  return activeVal > 0 && grid.value[r][c].value === activeVal
}

const toggleNoteMode = () => {
  isNoteMode.value = !isNoteMode.value
}

// 擦除选中格
const eraseSelected = () => {
  if (isCompleted.value || !selectedCell.value) return
  const { r, c } = selectedCell.value
  const cell = grid.value[r][c]
  if (cell.fixed) return

  cell.value = 0
  cell.notes = []
  validateGrid()
}

// 输入数字 (1-9)
const inputNumber = (num: number) => {
  if (isCompleted.value || !selectedCell.value) return
  const { r, c } = selectedCell.value
  const cell = grid.value[r][c]
  if (cell.fixed) return

  if (isNoteMode.value) {
    // 笔记模式
    if (cell.value > 0) cell.value = 0
    const idx = cell.notes.indexOf(num)
    if (idx >= 0) {
      cell.notes.splice(idx, 1)
    } else {
      cell.notes.push(num)
      cell.notes.sort((a, b) => a - b)
    }
  } else {
    // 正常填数
    cell.value = cell.value === num ? 0 : num
    cell.notes = []
    validateGrid()
    checkWin()
  }
}

// 校验全局冲突
const validateGrid = () => {
  // 重置冲突标志
  for (let r = 0; r < 9; r++) {
    for (let c = 0; c < 9; c++) {
      grid.value[r][c].isConflict = false
    }
  }

  // 行列校验
  for (let i = 0; i < 9; i++) {
    const rowSeen = new Map<number, Cell[]>()
    const colSeen = new Map<number, Cell[]>()
    for (let j = 0; j < 9; j++) {
      const rowVal = grid.value[i][j].value
      if (rowVal > 0) {
        if (!rowSeen.has(rowVal)) rowSeen.set(rowVal, [])
        rowSeen.get(rowVal)!.push(grid.value[i][j])
      }
      const colVal = grid.value[j][i].value
      if (colVal > 0) {
        if (!colSeen.has(colVal)) colSeen.set(colVal, [])
        colSeen.get(colVal)!.push(grid.value[j][i])
      }
    }
    for (const cells of rowSeen.values()) {
      if (cells.length > 1) {
        cells.forEach((c) => (c.isConflict = true))
      }
    }
    for (const cells of colSeen.values()) {
      if (cells.length > 1) {
        cells.forEach((c) => (c.isConflict = true))
      }
    }
  }

  // 3x3 宫校验
  for (let br = 0; br < 3; br++) {
    for (let bc = 0; bc < 3; bc++) {
      const boxSeen = new Map<number, Cell[]>()
      for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
          const cell = grid.value[br * 3 + r][bc * 3 + c]
          if (cell.value > 0) {
            if (!boxSeen.has(cell.value)) boxSeen.set(cell.value, [])
            boxSeen.get(cell.value)!.push(cell)
          }
        }
      }
      for (const cells of boxSeen.values()) {
        if (cells.length > 1) {
          cells.forEach((c) => (c.isConflict = true))
        }
      }
    }
  }
}

// 检查是否通关胜利
const checkWin = () => {
  for (let r = 0; r < 9; r++) {
    for (let c = 0; c < 9; c++) {
      const cell = grid.value[r][c]
      if (cell.value === 0 || cell.isConflict) return
    }
  }

  stopTimer()
  isCompleted.value = true
}

// 物理键盘支持
const handleKeyDown = (e: KeyboardEvent) => {
  if (isFormField(e) || isCompleted.value) return
  if (['1', '2', '3', '4', '5', '6', '7', '8', '9'].includes(e.key)) {
    inputNumber(parseInt(e.key, 10))
    return
  }
  if (e.key === 'Backspace' || e.key === 'Delete' || e.key === '0') {
    eraseSelected()
    return
  }
  if (e.key === 'n' || e.key === 'N') {
    toggleNoteMode()
    return
  }

  // 方向键移动光标
  if (['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown'].includes(e.key)) {
    e.preventDefault()
    if (!selectedCell.value) {
      selectedCell.value = { r: 0, c: 0 }
      return
    }
    let { r, c } = selectedCell.value
    if (e.key === 'ArrowLeft') c = (c + 8) % 9
    else if (e.key === 'ArrowRight') c = (c + 1) % 9
    else if (e.key === 'ArrowUp') r = (r + 8) % 9
    else if (e.key === 'ArrowDown') r = (r + 1) % 9
    selectedCell.value = { r, c }
  }
}

onMounted(() => {
  initNewGame()
  window.addEventListener('keydown', handleKeyDown)
})

onUnmounted(() => {
  stopTimer()
  window.removeEventListener('keydown', handleKeyDown)
})
</script>
